import React, { useEffect, useRef, useState } from 'react';
import Page from '../../../layout/Page';
import Row from '../../../layout/Row';
import Col from '../../../layout/Col';
import { QuestRoute } from '../../../api/QuestRoutes';
import Checkbox from '../../../forms/Checkbox';
import LoadingEllipsis from '../../../forms/LoadingEllipsis';
import { beginningOfMonth, exportToPDF, formatCurrency, generateId, inputDate, offsetUTC, planeDate } from '../../../utility/functions';
import Collapsible from '../../../layout/Collapsable';
import HamQuest from '../../../api/HamQuestGiver';
import Form from '../../../forms/Form';
import Button from '../../../forms/Button';
import styled from 'styled-components';
import Card from '../../../forms/Card';
import CompanyLogo from '../../../CompanyLogo';
import Heading from '../../../forms/Heading';
import Table from '../../../table/Table';
import LocModelDropdown from '../../../LocModelDropdown';
import Input from '../../../forms/Input';

const HeaderRow = styled.div`
    display: flex;
    gap: 4em;
    justify-content: space-between;
    align-items: flex-start;
`
const ReportSegment = styled.div`
    border-bottom: 1px solid ${({ theme }) => theme.gray}40;
    margin-bottom: 0.5em;
    padding-bottom: 0.2em;
`

const Revenue = ({ ...props }) => {

    const cardRef = useRef();
    const [models, setModels] = useState(null);
    const [locModels, setLocModels] = useState(null);
    const [revData, setRevenueData] = useState(null);
    const [revConfig, setRevConfig] = useState([]);
    const [locInfo, setLocInfo] = useState(null);
    const [ytd, setYtd] = useState(false);
    const [mtd, setMtd] = useState(false);

    const [startDate, setStartDate] = useState(beginningOfMonth);
    const [endDate, setEndDate] = useState(inputDate());

    useEffect(() => {
        HamQuest.getQuery(QuestRoute.reports.models, { startDate, endDate })
            .then(revenue => {
                setLocInfo(revenue)
                setModels([
                    ...Object.values(revenue).reduce((acc, location) => {
                        Object.keys(location).forEach(model => acc.add(model));
                        return acc;
                    }, new Set())
                ]);
            })
    }, [startDate, endDate])

    const TableDetails = ({ label, data }) => {

        const { isMTD, isYTD } = revConfig;
        const { records, currentSum, ytdSum, compSum, ytdCompSum } = data;

        return (
            <Table
                config={{
                    label: label,
                    disableTableControls: true,
                    disableSlideAnimation: true,
                    disableHeaderSearch: true,
                    hideNoResults: true,
                    data: records,
                    headers: isYTD ? ([
                        {
                            label: 'Item',
                            propKey: 'type',
                        }, {
                            label: 'Amount',
                            propKey: 'currentAmount',
                        }, {
                            label: 'Ratio',
                            propKey: 'currentRatio',
                        }, {
                            label: 'Amount',
                            propKey: 'ytdAmount',
                        }, {
                            label: 'Ratio',
                            propKey: 'ytdRatio',
                        }, {
                            label: 'Amount',
                            propKey: 'compAmount',
                        }, {
                            label: 'Ratio',
                            propKey: 'compRatio',
                        }, {
                            label: 'Amount',
                            propKey: 'ytdCompAmount',
                        }, {
                            label: 'Ratio',
                            propKey: 'ytdCompRatio',
                        },
                    ]) : isMTD ? ([
                        {
                            label: 'Item',
                            propKey: 'type',
                        }, {
                            label: 'Amount',
                            propKey: 'currentAmount',
                        }, {
                            label: 'Ratio',
                            propKey: 'currentRatio',
                        }, {
                            label: 'Amount',
                            propKey: 'compAmount',
                        }, {
                            label: 'Ratio',
                            propKey: 'compRatio',
                        },
                    ]) : ([
                        {
                            label: 'Item',
                            propKey: 'type',
                        }, {
                            label: 'Amount',
                            propKey: 'currentAmount',
                        }, {
                            label: 'Ratio',
                            propKey: 'currentRatio',
                        },
                    ]),
                    renderFormat: (e) => (
                        <tr key={generateId()}>
                            <td>{e.type}</td>
                            <td>{formatCurrency(e.currentAmount)}</td>
                            <td>{(e.currentRatio * 100).toFixed(1)}%</td>
                            {
                                isYTD ?
                                    <>
                                        <td>{formatCurrency(e.ytdAmount)}</td>
                                        <td>{(e.ytdRatio * 100).toFixed(1)}%</td>
                                        <td>{formatCurrency(e.compAmount)}</td>
                                        <td>{(e.compRatio * 100).toFixed(1)}%</td>
                                        <td>{formatCurrency(e.ytdCompAmount)}</td>
                                        <td>{(e.ytdCompRatio * 100).toFixed(1)}%</td>
                                    </>
                                    : isMTD ?
                                        <>
                                            <td>{formatCurrency(e.compAmount)}</td>
                                            <td>{(e.compRatio * 100).toFixed(1)}%</td>
                                        </>
                                        : null
                            }
                        </tr>
                    ),
                    firstHeaderRow: () => (
                        <tr>
                            <th></th>
                            <th colSpan={2}>Current</th>
                            {
                                isYTD ?
                                    <>
                                        <th colSpan={2}>Year To Date</th>
                                        <th colSpan={2}>Current Comparison</th>
                                        <th colSpan={2}>Year To Date Comparison</th>
                                    </>
                                    : null
                            }

                        </tr>
                    ),
                    finalRow: () => (
                        <tr key={generateId()}>
                            <td><b>Total</b></td>
                            <td>{formatCurrency(currentSum)}</td>
                            <td></td>
                            {
                                isYTD ?
                                    <>
                                        <td>{formatCurrency(ytdSum ?? 0)}</td>
                                        <td></td>
                                        <td>{formatCurrency(compSum ?? 0)}</td>
                                        <td></td>
                                        <td>{formatCurrency(ytdCompSum ?? 0)}</td>
                                    </> : null
                            }
                        </tr>
                    )
                }}
            />
        );
    }



    return (
        <Row align='flex-start' style={{ width: '100%' }} wrap>
            <Col style={{ width: '30%' }} >
                <Button
                    disabled={!revData}
                    onClick={() => exportToPDF(cardRef, `Revenue Report`)}
                >Export</Button>

                <Checkbox
                    label='Month to Date'
                    value={mtd}
                    onChange={(e) => {
                        setMtd(e.target.checked)
                        setYtd(false)
                    }}
                    standalone
                />
                <Checkbox
                    label='Year to Date'
                    value={ytd}
                    onChange={(e) => {
                        setYtd(e.target.checked)
                        setMtd(false)
                    }}
                    standalone
                />

                <Row>
                    <Input
                        label='Start Date'
                        name='startDate'
                        type='date'
                        required
                        value={startDate}
                        onInput={e => setStartDate(e.target.value)}
                        disable={ytd || mtd}
                        standalone
                    />
                    <Input
                        label='End Date'
                        name='endDate'
                        type='date'
                        value={endDate}
                        onInput={e => setEndDate(e.target.value)}
                        required
                        disable={ytd || mtd}
                        standalone
                    />
                </Row>
                <Form
                    inputs={{}}
                    onSubmit={(e) => {
                        setRevConfig({
                            locations: locModels.locations,
                            isMTD: mtd,
                            isYTD: ytd
                        });
                        return HamQuest.post(QuestRoute.reports.revenue, {
                            startDate,
                            endDate,
                            query: locModels.query,
                            ytd: ytd || mtd,
                        })
                            .then(res => setRevenueData(res))
                    }}
                    text='Search Locations'
                >
                    <LocModelDropdown startDate={startDate} endDate={endDate} getState={setLocModels} />
                </Form>

                {
                    models ?
                        <Form
                            inputs={{ models: [] }}
                            onSubmit={(e) => {
                                const query = [];
                                Object.entries(locInfo).forEach(([locationId, models]) => {
                                    Object.entries(models).forEach(([model, terminals]) => {
                                        if (e.models.includes(model)) {
                                            terminals.terminalIds.forEach(f => {
                                                query.push({
                                                    locationId: parseInt(locationId),
                                                    model,
                                                    terminalId: f
                                                })
                                            })
                                        }
                                    })
                                })
                                return HamQuest.post(QuestRoute.reports.revenue, {
                                    startDate,
                                    endDate,
                                    query: query,
                                    ytd: ytd || mtd,
                                })
                                    .then(res => setRevenueData(res))
                            }}
                            text='Search Models'
                        >
                            <Collapsible label={'Models'}>
                                {
                                    models && models.map((e, i) => (
                                        <Checkbox
                                            key={`${i}-model`}
                                            label={e}
                                            name={'models'}
                                            value={e}
                                            style={{ fontSize: '0.5em', }}
                                        />
                                    ))
                                }
                            </Collapsible>
                        </Form>
                        : <span>Loading Models<LoadingEllipsis /></span>
                }
            </Col>
            <Col style={{ width: '65%' }}>
                {revData ?
                    <Card title='Revenue Report' icon='clipboard'>
                        <div ref={cardRef}>
                            <HeaderRow style={{ marginBottom: '3em' }}>
                                <Col>
                                    <Heading>Period</Heading>
                                    <HeaderRow>
                                        <span>Start: {planeDate(offsetUTC(revData.startDate))}</span>
                                    </HeaderRow>
                                    <HeaderRow>
                                        <span>End: {planeDate(offsetUTC(revData.endDate))}</span>
                                    </HeaderRow>
                                </Col>
                                <CompanyLogo dim={6} />
                            </HeaderRow>
                            <ReportSegment>
                                <Col style={{ gap: '2em' }}>
                                    {revConfig.locations?.length ? (
                                        <HeaderRow>
                                            <Col>
                                                <HeaderRow>
                                                    <b>Locations:</b>
                                                    <Row>{revConfig.locations.join(', ')}</Row>
                                                </HeaderRow>
                                            </Col>
                                        </HeaderRow>
                                    ) : null}

                                    {/* <TableDetails
                                            label='Gross Sales by Item'
                                            data={revData.items}
                                        /> */}
                                    <TableDetails
                                        label='Income Detail'
                                        data={revData.payments}
                                    />
                                    <TableDetails
                                        label='Refund Detail'
                                        data={revData.refunds}
                                    />
                                    {/* <TableDetails
                                            label='Redeemed Detail'
                                            data={revData.discounts}
                                        /> */}
                                    <TableDetails
                                        label='Tax Detail'
                                        data={revData.taxes}
                                    />



                                </Col>

                            </ReportSegment>
                        </div>
                    </Card>
                    : null}
            </Col>
        </Row>
    );
}

export default Revenue;
